import moment from "moment";
import { AssetMainType, IAssetInfo } from "../models/asset";
import {
    formatPrivateBondAnalysisName,
    shortInstitutionName,
} from "./private-bond-subtype";

type ListRow = {
    listSubtitle?: string;
    mainType?: string;
    issuingBankName?: string;
    negotiatingBankName?: string;
    indexingAgent?: { name?: string; tax?: number } | null;
    quantity?: number | null;
    brand?: string;
    vehicleType?: string;
    propertyType?: string;
    asset?: { dueDate?: Date | null; finIssueName?: string | null };
};

export const getAssetDetailsHeading = (info: IAssetInfo | null | undefined): { title: string; subtitle: string } => {
    if (!info) {
        return { title: "Ativo", subtitle: "" };
    }

    const type = info.asset_type?.toLowerCase();
    const bank = shortInstitutionName(
        info.asset_fin_issue?.inst_name ?? info.asset_fin_negotiating?.inst_name
    );

    switch (type) {
        case AssetMainType.PrivateBound:
            return {
                title: formatPrivateBondAnalysisName(info.asset_pb_details?.pb_type, info),
                subtitle: bank,
            };
        case AssetMainType.TreasureBound:
            return {
                title: info.asset_tb_details?.treasure?.TrsrBd?.nm ?? "Tesouro Direto",
                subtitle: bank,
            };
        case AssetMainType.Debenture:
            return {
                title: info.asset_db_details?.db_code
                    ? `Debênture · ${info.asset_db_details.db_code}`
                    : "Debênture",
                subtitle: [
                    info.asset_db_details?.debenture?.deb_company,
                    bank,
                ].filter(Boolean).join(" · "),
            };
        case AssetMainType.SavingAccount:
            return { title: "Poupança", subtitle: bank };
        case AssetMainType.CheckingAccount: {
            const cdi = info.asset_ca_details?.ca_rate_cdi;
            const index = cdi != null && cdi > 0
                ? `${String(cdi).replace(".", ",")}% do CDI`
                : "";
            return {
                title: info.asset_alias?.trim() || "Conta Corrente",
                subtitle: [index, bank].filter(Boolean).join(" · "),
            };
        }
        case AssetMainType.Stock:
            return {
                title: info.asset_st_details?.st_ticker_symbol || "Ação",
                subtitle: bank,
            };
        case AssetMainType.InvestimentFunds:
            return {
                title: info.asset_if_details?.if_ticker_symbol || "Fundo",
                subtitle: bank,
            };
        case AssetMainType.Currency:
            return {
                title: info.asset_cy_details?.currency_symbol?.name || "Moeda",
                subtitle: bank,
            };
        case AssetMainType.Cripto:
            return {
                title: info.asset_cm_details?.cripto_symbol?.currency_base || "Cripto",
                subtitle: bank,
            };
        case AssetMainType.Customized:
            return {
                title: info.asset_alias?.trim() || "Personalizado",
                subtitle: bank,
            };
        default:
            return { title: info.asset_alias?.trim() || "Ativo", subtitle: bank };
    }
};

export const getAssetListSubtitle = (row: ListRow): string | undefined => {
    if (row.listSubtitle) {
        return row.listSubtitle;
    }

    const bank = shortInstitutionName(
        row.issuingBankName || row.asset?.finIssueName || row.negotiatingBankName
    );
    const due = row.asset?.dueDate
        ? `vence ${moment(row.asset.dueDate).format("MM/YYYY")}`
        : "";

    switch (row.mainType) {
        case AssetMainType.CheckingAccount: {
            const tax = row.indexingAgent?.tax;
            const index = tax != null && tax > 0
                ? `${String(tax).replace(".", ",")}% do CDI`
                : "";
            return [index, bank].filter(Boolean).join(" · ") || undefined;
        }
        case AssetMainType.SavingAccount:
            return bank || undefined;
        case AssetMainType.TreasureBound:
        case AssetMainType.Debenture:
            return [due, bank].filter(Boolean).join(" · ") || undefined;
        case AssetMainType.Stock:
        case AssetMainType.InvestimentFunds:
        case AssetMainType.Currency:
        case AssetMainType.Cripto: {
            const qty = row.quantity != null ? `${row.quantity} un.` : "";
            return [qty, bank].filter(Boolean).join(" · ") || undefined;
        }
        case AssetMainType.Property:
            return row.propertyType || undefined;
        case AssetMainType.Vehicle:
            return [row.brand, row.vehicleType].filter(Boolean).join(" · ") || undefined;
        default:
            return bank || undefined;
    }
};

const formatMonthSpan = (totalMonths: number): string => {
    const years = Math.floor(Math.abs(totalMonths) / 12);
    const months = Math.abs(totalMonths) % 12;
    const parts: string[] = [];
    if (years > 0) {
        parts.push(`${years} ${years === 1 ? "ano" : "anos"}`);
    }
    if (months > 0) {
        parts.push(`${months} ${months === 1 ? "mês" : "meses"}`);
    }
    return parts.join(" e ") || "menos de 1 mês";
};

export const formatTenure = (purchaseDate?: string | null): string | null => {
    if (!purchaseDate) {
        return null;
    }
    const start = moment(purchaseDate).startOf("day");
    if (!start.isValid()) {
        return null;
    }
    const months = moment().startOf("day").diff(start, "months");
    if (months < 0) {
        return null;
    }
    return formatMonthSpan(months);
};

export const formatTimeToDue = (dueDate?: string | null): string | null => {
    if (!dueDate) {
        return null;
    }
    const end = moment(dueDate).startOf("day");
    if (!end.isValid()) {
        return null;
    }
    const months = end.diff(moment().startOf("day"), "months");
    if (months < 0) {
        return `vencido há ${formatMonthSpan(months)}`;
    }
    return formatMonthSpan(months);
};
